import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { AppRegistryPage } from '@plato/app-meta/ai-meta';
import { usePageAiMeta } from '@plato/app-shared/hooks/use-page-ai-meta';
import { SrSalesOrderType, type SrOrderStatus } from '@plato/data/starrocks/types';
import { OrderIcon } from '@plato/ui-legacy/assets/icons/order-icon';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import type { QuickFilter } from '@plato/ui-legacy/components/filters/lib/filter-utils';
import { ListView } from '@plato/ui-legacy/components/list-views/list-view';
import { TableFooterWithCount } from '@plato/ui-legacy/components/tables/table-footer-with-count';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { DataTable } from '@plato/ui/components/data-table';
import { PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle } from '../../components/page-title.tsx';
import { useAnalytics } from '../../features/analytics/hooks/use-analytics.ts';
import { useOrderColumnsForListGrid } from '../../features/list-views/columns/use-order-columns-for-list-grid.tsx';
import { useGenericListViewSorting } from '../../features/list-views/hooks/use-generic-list-view-sorting.tsx';
import { QuoteOrderListFilters } from '../../features/quote-order-list/components/quote-order-list-filters.tsx';
import {
    billedOrdersFilter,
    openOrdersFilter,
    useQuoteOrderListFilters,
    type QuoteOrderQueryFilters,
} from '../../features/quote-order-list/hooks/use-quote-order-list-filters.tsx';
import { useQuoteOrderListSummary } from '../../features/quote-order-list/hooks/use-quote-order-list-summary.tsx';
import { DraftOrderCreateDialog } from '../../features/sales-draft-new/views/create-draft-order-dialog.tsx';
import type { QuoteOrderSort } from '../../server/types/list-view-types.ts';
import { SortOrder, type QuotesOrdersSortBy } from '../../server/types/list-view-types.ts';
import { api } from '../../utils/api.ts';

export function useOrderListAiMeta(): AppRegistryPage {
    const { t } = useTranslation();
    return usePageAiMeta(
        {
            path: '/orders',
            title: t('page_title__sales_orders', { ns: 'common' }),
            requires: ['canReadOrders'],
            description:
                'All sales orders from the ERP in one filterable list. Send the user here to find an ' +
                'order or to see orders by status, date, type, sales rep, storage or sales location or ' +
                'amount. The search box matches customer and order id. Opening an order shows its ' +
                'detail page. Some tenants do not have this page; if the user cannot see it, it is not ' +
                'enabled for them.',
        },
        useOrderColumnsForListGrid(),
    );
}

const ORDER_TYPE = SrSalesOrderType.ORDER;

function QuotesListView() {
    const { t } = useTranslation();
    const {
        canFilterQuoteOrderByStatus,
        openOrderStatuses,
        transactionDraftAvailability,
        canReadOpenOrders,
    } = useAppConfig();
    const sendEvent = useAnalytics();
    const columns = useOrderColumnsForListGrid();

    const quickFilters = [] as QuickFilter<QuoteOrderQueryFilters>[];
    if (canReadOpenOrders) {
        quickFilters.push({
            id: 'my-open-orders',
            label: t('quick_filters__orders_my_open_orders', { ns: 'filters' }),
            showByDefault: true,
            filter: { ...openOrdersFilter, orderStatus: openOrderStatuses },
        });
    }

    if (canFilterQuoteOrderByStatus) {
        quickFilters.push({
            id: 'my-billed-orders',
            label: t('quick_filters__orders_my_billed_orders', { ns: 'filters' }),
            showByDefault: true,
            filter: billedOrdersFilter,
        });
    }

    const [appliedFilters, setAppliedFilters] = useQuoteOrderListFilters(ORDER_TYPE);

    const [sorting, sortChangedInInterface] = useGenericListViewSorting<QuotesOrdersSortBy>(
        appliedFilters.sortingFilter,
        (sortingFilter: QuoteOrderSort | null) => {
            setAppliedFilters((filters) => ({
                ...filters,
                sortingFilter: sortingFilter ?? undefined,
            }));
            if (sortingFilter) {
                sendEvent('quote_order_list_sort_used', {
                    column: sortingFilter.field,
                    direction: sortingFilter.direction === SortOrder.DESC ? 'DESC' : 'ASC',
                });
            }
        },
    );

    const { summary } = useQuoteOrderListSummary(ORDER_TYPE);

    const { data: aggregates, isLoading: isLoadingCount } =
        api.platoUser.getManyQuotesOrders.getAggregates.useQuery({
            ...appliedFilters,
            orderStatus: appliedFilters.orderStatus as SrOrderStatus[],
            orderTypes: [ORDER_TYPE],
        });
    const count = aggregates?.count;

    const {
        data: ordersListRawData,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        isSuccess: isDoneGettingList,
    } = api.platoUser.getManyQuotesOrders.getWithFilters.useInfiniteQuery(
        { ...appliedFilters, orderTypes: [ORDER_TYPE] },
        {
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const quotesList = useMemo(() => {
        if (ordersListRawData) {
            return ordersListRawData.pages.flatMap((page) => page.result);
        } else {
            return [];
        }
    }, [ordersListRawData]);

    return (
        <Page>
            <PageTitle title={t('page_title__sales_orders')} />
            <PageHeader
                icon={OrderIcon}
                subTitle={t('page_sub_title__sales_orders')}
                title={t('page_title__sales_orders')}
                actions={
                    transactionDraftAvailability !== 'UNAVAILABLE' && (
                        <Button
                            variant="primary"
                            onClick={() => {
                                void DialogStateManager.show(DraftOrderCreateDialog, {
                                    draftType: 'ORDER',
                                    source: 'order_list',
                                });
                            }}
                            icon={PlusIcon}
                        >
                            <span>{t('top_navbar__new_draft')}</span>
                        </Button>
                    )
                }
            />
            <QuoteOrderListFilters
                orderType={ORDER_TYPE}
                quickFilters={quickFilters}
                canGetFilters={isDoneGettingList}
            />
            <ListView>
                <ListView.BodyContainer>
                    <DataTable
                        emptyLabel={t('empty_table__no_results')}
                        isLoading={isLoading}
                        isFetchingNextPage={isFetchingNextPage}
                        onFetchNextPage={fetchNextPage}
                        hasNextPage={hasNextPage}
                        data={quotesList}
                        columns={columns}
                        manualSorting
                        onSortingChange={sortChangedInInterface}
                        sorting={sorting}
                        summary={summary}
                    />
                    <TableFooterWithCount loading={isLoadingCount} count={count} />
                </ListView.BodyContainer>
            </ListView>
        </Page>
    );
}

export default QuotesListView;
